import React from 'react';
import { Box, Typography, Paper, Button, CircularProgress } from '@mui/material';
import LockIcon from '@mui/icons-material/Lock';
import RefreshIcon from '@mui/icons-material/Refresh';
import { useMutation } from '@apollo/client';
import { PlanStep, StepStatus } from '../../../../types/generated/graphql';
import { colors } from '../../../../theme/colors';
import CollapsibleStepInfo from '../CollapsibleStepInfo';
import { AsyncConnectorActivityCard } from '../AsyncConnectorActivityCard';
import { EmailThreadPanel } from '../EmailThreadPanel';
import { PortalStatusPanel } from '../PortalStatusPanel';
import { SignerChecklistPanel } from '../SignerChecklistPanel';
import { CalendarEventPanel } from '../CalendarEventPanel';
import { RETRY_CONNECTOR_STEP, GET_PLAN } from '../../../../graphql/queries';
import { useToast } from '../../../../contexts/ToastContext';

interface WaitingExternalContentProps {
  planId: string;
  step: PlanStep;
}

/**
 * WAITING_EXTERNAL / BLOCKED_CONNECTOR panel content.
 * Shows a locked state: no form, no Submit, no editable fields.
 * Displays activity timeline and result (or block reason) via AsyncConnectorActivityCard.
 * When blocked and lastConnectorSubmitInput exists, shows Resubmit button.
 */
export const WaitingExternalContent: React.FC<WaitingExternalContentProps> = ({ planId, step }) => {
  const isBlocked = step.status === StepStatus.Blocked;
  const ctx = step.context as { lastConnectorSubmitInput?: unknown } | null | undefined;
  const canResubmit = isBlocked && ctx?.lastConnectorSubmitInput != null;

  const { showToast } = useToast();
  const [retryConnectorStep, { loading: resubmitting }] = useMutation(RETRY_CONNECTOR_STEP, {
    refetchQueries: [{ query: GET_PLAN, variables: { id: planId } }],
    onCompleted: () => {
      showToast({ message: 'Request resubmitted. Waiting for response.', severity: 'success', autoHideDuration: 4000 });
    },
    onError: (err) => {
      showToast({ message: err.message || 'Resubmit failed', severity: 'error', autoHideDuration: 6000 });
    },
  });

  const handleResubmit = () => {
    retryConnectorStep({ variables: { planId, stepId: step.id } });
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', flex: 1 }}>
      {step.description && (
        <CollapsibleStepInfo stepId={step.id} description={step.description} />
      )}

      {/* Locked state message */}
      <Paper
        variant="outlined"
        sx={{
          p: 2,
          mx: 2,
          mt: 1,
          mb: 1.5,
          display: 'flex',
          alignItems: 'center',
          gap: 1.5,
          bgcolor: isBlocked ? colors.red[50] : colors.amber[50],
          borderLeft: `4px solid ${isBlocked ? colors.red[600] : colors.amber[400]}`,
        }}
      >
        <LockIcon sx={{ color: isBlocked ? colors.red[600] : colors.amber[600], fontSize: 28 }} />
        <Box sx={{ flex: 1 }}>
          <Typography variant="subtitle2" fontWeight={600}>
            {isBlocked ? 'Step is blocked' : 'Waiting for response'}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {isBlocked
              ? 'This step cannot be completed until the issue is resolved. See details below.'
              : 'The request has been submitted. You cannot resubmit or edit while we wait for a response.'}
          </Typography>
        </Box>
      </Paper>

      {/* Portal status panel (when step has external submission context) */}
      {(step.context as any)?.externalSubmission && (
        <PortalStatusPanel submission={(step.context as any).externalSubmission} />
      )}

      {/* Email thread panel (when step has email context) */}
      {(step.context as any)?.emailThreadId && (
        <EmailThreadPanel
          emailThreadId={(step.context as any)?.emailThreadId}
          emailReplies={(step.context as any)?.collectedData?.emailReplies}
          emailEvents={(step.context as any)?.emailEvents}
          recipientEmail={(step.context as any)?.recipientEmail}
        />
      )}

      {/* Signer checklist (when step has e-sign context) */}
      {(step.context as any)?.esign && (
        <SignerChecklistPanel esign={(step.context as any).esign} />
      )}

      {/* Calendar event panel (when step has calendar context) */}
      {(step.context as any)?.calendarEvent && (
        <CalendarEventPanel calendarEvent={(step.context as any).calendarEvent} />
      )}

      {/* Activity timeline + result / block reason */}
      <Box sx={{ px: 2, pb: canResubmit ? 1 : 2 }}>
        <AsyncConnectorActivityCard step={step} compact={false} />
      </Box>

      {/* Resubmit after activity card (Option D: read then act) */}
      {canResubmit && (
        <Box sx={{ px: 2, pb: 2 }}>
          <Button
            variant="contained"
            size="medium"
            startIcon={resubmitting ? <CircularProgress size={18} color="inherit" /> : <RefreshIcon />}
            onClick={handleResubmit}
            disabled={resubmitting}
          >
            {resubmitting ? 'Resubmitting...' : 'Resubmit'}
          </Button>
        </Box>
      )}
    </Box>
  );
};

export default WaitingExternalContent;
